import { mapState } from 'vuex';
import { callApi } from '@/api';
import LogService from '@/services/LogService';
import { camelCase } from 'lodash';
import { addAmPm, getLanguageFromLanguageParam } from '@/common/common';
import configEnv from "@/enums/configEnv";

const common = {
  computed: {
    ...mapState(['lang']),
    phoneCodes() {
      return [
        {
          code: '49',
          text: '+49',
          country: 'Deutschland',
          flag: '',
        },
      ];
    },
    routeLang() {
      return this.$route.params?.lang || '';
    },
  },
  methods: {
    async getWordPressStrings(sectionArray, pageTitleSection = undefined, selector = 'title', lang = undefined) {
      LogService.log('calling wp strings');
      await this.$wpAPI.getStringsArray(
        sectionArray,
        lang ?? this.routeLang,
      ).then((results) => {
        LogService.log('wp strings returned', results);
        this.str = {
          ...this.str,
          ...results,
        };
        if (pageTitleSection !== undefined) {
          const pageTitle = this.str[camelCase(pageTitleSection)][selector];
          this.$store.commit('SET_PAGE_TITLE', pageTitle);
          LogService.log('getWordPressStrings, set pageTitle', pageTitle);
        }
      });
    },
    async saveBeforeLeave() {
      // save changes in api
      const infoApi = '/api/order/';
      await this.callApi({url: infoApi, method: 'post'});
    },
    callApi(o) {
      return callApi(o, this.$store);
    },
    checkIfRedirectToEnglishNecessary() {
      const isBot = () => {
        if (typeof window === 'undefined') return false;
        const userAgent = navigator.userAgent.toLowerCase();
        const botPattern = new RegExp(
          'bot|googlebot|crawler|spider|robot|bingbot|'
          + 'google-inspectiontool|lighthouse|facebookexternalhit|'
          + 'twitterbot',
          'i',
        );
        return botPattern.test(userAgent);
      };
      if (isBot) {
        return false;
      }
      const {locale} = Intl.NumberFormat().resolvedOptions();
      const supportedLocales = navigator.languages;
      const {lang} = this.$route.params;
      const {just_arrived} = this.$route.query;
      const deSupported = supportedLocales.some((x) => x.startsWith('de'));
      if (just_arrived && locale !== undefined && locale.startsWith('en') && !deSupported && lang === 'de') {
        const {query} = this.$route;
        if ('just_arrived' in query) {
          delete query.just_arrived;
        }
        this.$router.replace({
          name: this.$route.name,
          params: {...this.$route.params, lang: 'en'},
          query
        });
        return true;
      }
      return false;
    },
    format(template, ...args) {
      // formatting string such as 'lorem %s ispum' and replace %s to param from args
      let n = -1;
      return template && template.replace(/(%s)/g, () => {
        n += 1;
        return args[n];
      });
    },
    formatHourAmPm(hour, paramLang) {
      const lang = getLanguageFromLanguageParam(paramLang);
      return lang === 'en' ? addAmPm(hour) : String(hour);
    },
    formatAdjacentHours(start, end, paramLang) {
      const lang = getLanguageFromLanguageParam(paramLang);
      if (lang === 'en') {
        return this.format(
          this.str.orderInfo?.between_hours_adjacent,
          addAmPm(start),
          addAmPm(end),
        );
      }
      return this.format(
        this.str.orderInfo?.between_hours_adjacent,
        start,
        end,
      );
    },
    getDiffInDays(date1, date2) {
      const oneDay = 24 * 60 * 60 * 1000; // Milliseconds in a day
      const diffInTime = Math.abs(date2.getTime() - date1.getTime());
      return Math.ceil(diffInTime / oneDay);
    },
    getValidatedServerType() {
      const serverType = configEnv.server_type;
      return serverType.toLowerCase(); // z.B. "wetterheld" für Ordnerpfade
    },
    async getPageStrings(sectionArray, titleSection, externalLang = undefined) {
      LogService.log('calling json strings, with arguments', sectionArray, titleSection);

      const serverType = this.getValidatedServerType();

      let targetLang = this.$route?.params?.lang;
      if (externalLang !== undefined) {
        targetLang = externalLang;
      }
      if (!targetLang || targetLang === '') {
        targetLang = configEnv.default_wp_language;
      }

      // Erstelle ein Array von Promises für parallele Ausführung
      const tasks = sectionArray.map(async (section) => {
        const languageToUse = configEnv.one_version_json_content_files.includes(section)
          ? 'all'
          : targetLang;

        LogService.log("section, lang", section, languageToUse);
        const cacheKey = `${serverType}_${section}_${languageToUse}`;

        let sectionData = this.$store.state.contentCache?.[cacheKey];

        if (!sectionData) {
          LogService.log(`Fetching section "${section}" (${languageToUse}) from JSON file...`);

          try {
            const contentPath = configEnv?.content_path || configEnv.content_path;
            const url = `${contentPath}${section}/${languageToUse}.json`;
            LogService.log(`Fetching section "${section}" (${languageToUse}) from URL: ${url}`);

            const response = await this.$axios.get(url);
            sectionData = response.data;

            this.$store.commit('SET_CONTENT_CACHE', {
              key: cacheKey,
              data: sectionData
            });
          } catch (error) {
            LogService.log(`Error loading section "${section}" (${languageToUse})`, error);
            sectionData = {};
          }
        } else {
          LogService.log(`Serving section "${section}" (${languageToUse}) from Store Vault!`);
        }

        this.$store.commit('SET_STRINGS', {
          group: camelCase(section),
          strings: sectionData,
        });

        return {
          key: camelCase(section),
          data: sectionData
        };
      });

      // Alle HTTP-Requests parallel abfeuern und auf Ergebnisse warten
      const results = await Promise.all(tasks);

      // Ergebnisse in ein Objekt überführen
      const combinedResults = {};
      results.forEach(({key, data}) => {
        combinedResults[key] = data;
      });

      if (titleSection) {
        const pageTitle = combinedResults[camelCase(titleSection)]?.['title'];
        if (pageTitle) {
          this.$store.commit('SET_PAGE_TITLE', pageTitle);
          LogService.log('getPageStrings, set pageTitle', pageTitle);
        }
      }

      // Sicheres Zusammenführen (unter Beachtung von Object.freeze)
      const currentStr = this.str ? {...this.str} : {};
      const updatedData = Object.freeze({
        ...currentStr,
        ...combinedResults,
      });

      this.$set(this, 'str', updatedData);
    }
  }
};

export default common;
